import { SupportSheet, type SupportAttribution, type SupportMode } from '@/components/support/SupportSheet';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface SupportContextValue {
  openSupport: (mode: SupportMode, attribution?: SupportAttribution) => void;
}

const SupportContext = createContext<SupportContextValue | null>(null);

export function useSupport() {
  const ctx = useContext(SupportContext);

  if (!ctx) throw new Error('useSupport must be used within a SupportProvider');
  return ctx;
}

export function SupportProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<SupportMode>('support');
  const [attribution, setAttribution] = useState<SupportAttribution>();

  const openSupport = useCallback((next: SupportMode, attr?: SupportAttribution) => {
    setMode(next);
    setAttribution(attr);
    setOpen(true);
  }, []);

  const value = useMemo(() => ({ openSupport }), [openSupport]);

  return (
    <SupportContext.Provider value={value}>
      {children}
      <SupportSheet open={open} mode={mode} attribution={attribution} onOpenChange={setOpen} />
    </SupportContext.Provider>
  );
}
